// i18n context — provides the active language, a setter (persisted to
// localStorage) and helpers: t(path, vars) for static nested copy and
// tm(map, key) for dynamic value maps (sections, messages, difficulty, ...).

import React, { createContext, useContext, useCallback, useMemo, useState } from "react";
import { translations, Lang, Dict } from "@/i18n/translations";

const KEY = "adaptivejam.lang";

function initialLang(): Lang {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === "de" || saved === "en") return saved;
  } catch {
    /* ignore */
  }
  return "de"; // German is the default
}

function resolvePath(obj: any, path: string): any {
  return path.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
}

function interpolate(str: string, vars?: Record<string, string | number>): string {
  if (!vars) return str;
  return str.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : `{${k}}`));
}

interface I18nValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  dict: Dict;
  t: (path: string, vars?: Record<string, string | number>) => string;
  tm: (map: keyof Dict, key: string) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Lang>(initialLang);
  const dict = translations[lang];

  const setLang = useCallback((l: Lang) => {
    localStorage.setItem(KEY, l);
    setLangState(l);
  }, []);

  const t = useCallback(
    (path: string, vars?: Record<string, string | number>) => {
      const val = resolvePath(dict, path);
      if (typeof val === "string") return interpolate(val, vars);
      return path;
    },
    [dict]
  );

  const tm = useCallback(
    (map: keyof Dict, key: string) => {
      const m = dict[map] as Record<string, string>;
      return (m && m[key]) || key;
    },
    [dict]
  );

  const value = useMemo(() => ({ lang, setLang, dict, t, tm }), [lang, setLang, dict, t, tm]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
